import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import { FiPlus } from 'react-icons/fi';
import {
  IoIosArrowBack,
  IoIosTrash,
  IoMdInformationCircle,
} from 'react-icons/io';
import { useHistory, useParams } from 'react-router-dom';
import { MoonLoader } from 'react-spinners';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

import { ImageLogoAtomBlack } from '~/assets/images';
import { ButtonBlack, Input, Select, Textarea } from '~/components';
import { useMuscleGroup } from '~/hooks/muscleGroup';
import { usePowerCreator } from '~/hooks/powerCreator';
import { usePrescription } from '~/hooks/prescription';
import { useSequency } from '~/hooks/sequency';
import { useStudent } from '~/hooks/student';
import { PowerCreator } from '~/models/PowerCreator';
import { SequenciesResponse, Sequency } from '~/models/Sequencies';
import api from '~/services/api';
import { formatHMS } from '~/utils';
import { formatProfileCode } from '~/utils/formatProfileCode';
import getSeconds from '~/utils/getSeconds';
import getValidationErrors from '~/utils/getValidationErrors';

import { IProgram } from '../../../interface';
import Modal from './Modal';
import SequenceCard from './SequenceCard';
import {
  TrainingStoresRequestData,
  DataFormProps,
  DivisionData,
  ExEditorsResponse,
  SelectProps,
  RouteParams,
  Props,
  InitialFormProps,
  DivisionDataResults,
} from './types';

import {
  ModalContainer,
  PageHeader,
  HeaderWrapper,
  PageHeaderBackButton,
  PageHeaderBackButtonLabel,
  InfoButton,
  Info,
  ContentContainer,
  InfoContent,
  RowContent,
  Section,
  NewButtonContainer,
  NewButton,
  AlertContainer,
  NewButtonLabel,
  Message,
  ModalSequenceContainer,
  FieldSet,
  FieldSetWrapper,
  FieldTitle,
  CardContainer,
  RowContainer,
  InputLabel,
  InputSet,
  RadioInput,
  SecondPageHeader,
  SecondaryHeaderLine,
  SecondaryHeaderBackButton,
  SecondaryHeaderContinueButton,
  SecondaryHeaderInfo,
  PowerCreatorButton,
  PowerCreatorView,
  Divider,
  RecoveryTimeModalContainer,
  DeleteModalContainer,
  FirstScreenHeaderContainer,
} from './styles';

export function PrescriptionScreen({ lockNextScreen }: Props) {
  const { goBack, location } = useHistory();
  const { userId: studentId, billingId } = useParams<RouteParams>();
  const { studentData, updateStudent } = useStudent();
  const { sequencyLabel, selectedSequency, updateSequencies } = useSequency();
  const { prescription } = usePrescription();
  const { groups } = useMuscleGroup();
  const { powerCreators } = usePowerCreator();

  const { division, programId } = useState(
    location.state as {
      division: DivisionDataResults;
      programId: number;
    },
  )[0];

  const [program, setProgram] = useState({} as IProgram);

  const [loading, setLoading] = useState(true);
  const [panelOfControlId, setPanelOfControlId] = useState<number>();
  const [totalNumberOfSeries, setTotalNumberOfSeries] = useState(0);
  const [sequencies, setSequencies] = useState<Sequency[]>([]);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [screenSwitch, setScreenSwitch] = useState(true); // true = firstScreen false = secondScreen
  const [showSequencesModal, setShowSequencesModal] = useState(false);
  const [showDeleteSequencyModal, setShowDeleteSequencyModal] = useState(false);

  // Check sequencies finalized
  useEffect(() => {
    const b =
      sequencies.length &&
      sequencies
        .map((sequency) =>
          sequency.prescriptions.map(
            (presc) =>
              Object.values(presc).filter((value) => value === '').length <= 7,
          ),
        )
        .flat()
        .filter((value) => value === false);

    if (b === 0) {
      lockNextScreen(true);
    } else if (b && b.length > 0) {
      lockNextScreen(true);
    } else {
      lockNextScreen(false);
    }
  }, [sequencies, lockNextScreen]);

  const handleGetDivisions = useCallback(async () => {
    const response = await api.get<DivisionData>(
      `training-programs/${programId}/divisions`,
    );

    setProgram(response.data.header);
  }, [programId]);

  useEffect(() => {
    handleGetDivisions();
  }, [handleGetDivisions]);

  // pega id da sequencia de treinamento
  useEffect(() => {
    async function fetchData() {
      const pocId = program?.controlPanel?.id;

      pocId && setPanelOfControlId(pocId);
    }

    fetchData();
  }, [program]);

  const loadSequencies = useCallback(async () => {
    const { data } = await api.get<SequenciesResponse>(
      `training-programs/${division.id}/divisions/${division.id}/sequencies`,
    );

    setSequencies(data.results);

    if (data.results.length > 0) {
      updateSequencies(division.id as number);
    }

    try {
      const numberOfSeries = data?.results
        .map((seq) => seq?.prescriptions.map((presc) => presc.number_of_series))
        .flat()
        .reduce((prev, next) => Number(prev) + Number(next));

      setTotalNumberOfSeries(numberOfSeries || 0);
    } catch (err) {
      console.log(err);
    }

    setLoading(false);
  }, [division.id]);

  useEffect(() => {
    updateStudent(studentId);
    loadSequencies();
  }, [updateStudent, studentId, loadSequencies]);

  function InfoModal() {
    const genderVariations: { [key: string]: string } = {
      male: 'MASCULINO',
      female: 'FEMININO',
      others: 'OUTRO',
      other: 'OUTRO',
      '': 'Não informado',
      null: 'Não informado',
    };

    return (
      <Modal
        isOpen={showInfoModal}
        setOpen={setShowInfoModal}
        maxWidthStyled={600}
        maxHeightStyled={600}
        zIndex={9999}
      >
        <ModalContainer>
          <h1>Informações do Treino</h1>

          <ContentContainer>
            <RowContent>
              <strong>Fatura: </strong>
              <p>{billingId}</p>
            </RowContent>

            <RowContent>
              <strong>Programa: </strong>
              {programId}
            </RowContent>

            <RowContent>
              <strong>Nome do aluno: </strong>
              {studentData.name}
            </RowContent>

            <RowContent>
              <strong>Sexo: </strong>
              {genderVariations[studentData.student.genre]}
            </RowContent>

            <RowContent>
              <strong>Nível do aluno: </strong>
              {program?.datasheet?.student_level}
            </RowContent>

            <RowContent>
              <strong>Avaliação: </strong>
              {program?.datasheet?.classification}
            </RowContent>

            <RowContent>
              <strong>Objetivo: </strong>
              {program?.datasheet?.objective}
            </RowContent>

            <RowContent>
              <strong>Frequência: </strong>
              {program?.datasheet?.frequency}
            </RowContent>

            <RowContent>
              <strong>Tempo disponível: </strong>
              {program?.datasheet?.available_time}
            </RowContent>

            <RowContent>
              <strong>Divisão de treino: </strong>
              {program?.training_division}
            </RowContent>
          </ContentContainer>

          <div className="profile-code">
            <h2>Profile Code:</h2>
            <h3>{formatProfileCode(program?.datasheet?.profile_code)}</h3>
          </div>

          <div className="sheet-model">
            <h2>Modelo de ficha:</h2>
            <h3>{program?.data_model}</h3>
          </div>
        </ModalContainer>
      </Modal>
    );
  }

  function SequenceModal() {
    const [radioStatusOptions, setRadioStatusOptions] = useState('1');
    const [loadingSM, setLoadingSM] = useState(false);

    const radioStatusOptionsList = useMemo(
      () => ['1', '1 bi-set', '1 tri-set'],
      [],
    );

    const RadioStatusOptions = useMemo(
      () =>
        radioStatusOptionsList.map((item) => (
          <RadioInput
            key={item}
            isSelected={item === radioStatusOptions}
            onClick={() => setRadioStatusOptions(item)}
          >
            <div className="radio">
              <div className="filled" />
            </div>

            <p>{item}</p>
          </RadioInput>
        )),
      [radioStatusOptionsList, radioStatusOptions],
    );

    const handleSubmit = useCallback(async () => {
      setLoadingSM(true);

      const data = {
        description: radioStatusOptions,
        number: radioStatusOptionsList.indexOf(radioStatusOptions) + 1,
      };

      try {
        await api.post(
          `training-programs/${division.id}/divisions/${division.id}/sequencies`,
          data,
        );

        toast.success(`A sequência foi gerada`);

        await loadSequencies();

        setShowSequencesModal(false);
      } catch (err) {
        console.log(err);

        toast.error(`Tente novamente`);
      }

      setLoadingSM(false);
    }, [radioStatusOptions, radioStatusOptionsList]);

    return (
      <Modal
        isOpen={showSequencesModal}
        setOpen={setShowSequencesModal}
        maxWidthStyled={621}
        maxHeightStyled={361}
      >
        <ModalSequenceContainer>
          <FieldSet>
            <FieldSetWrapper>
              <FieldTitle>Tipo de sequência:</FieldTitle>
              {RadioStatusOptions}
            </FieldSetWrapper>
          </FieldSet>

          <ButtonBlack disabled={loadingSM} onClick={handleSubmit}>
            Selecionar
          </ButtonBlack>
        </ModalSequenceContainer>
      </Modal>
    );
  }

  const loadSequenciesComponent = useMemo(
    () => (
      <Section>
        {sequencies.length ? (
          <CardContainer>
            {sequencies.map((sequency, index) => {
              return sequency.prescriptions.map(
                (prescription_, prescriptionIndex) => {
                  const isChecked: boolean =
                    (prescription_?.exercise &&
                      prescription_.exercise.length > 0) ||
                    false;

                  return (
                    <SequenceCard
                      key={prescription_.id}
                      sequencyNumber={index + 1}
                      prescriptionNumber={prescriptionIndex}
                      switchScreen={setScreenSwitch}
                      sequency={sequency}
                      completed={isChecked}
                      prescription={prescription_}
                    />
                  );
                },
              );
            })}
          </CardContainer>
        ) : (
          <Message>Nenhuma sequência cadastrada.</Message>
        )}
      </Section>
    ),
    [sequencies],
  );

  function FirstScreen() {
    return (
      <>
        <PageHeader>
          <HeaderWrapper>
            <FirstScreenHeaderContainer>
              <PageHeaderBackButton onClick={() => goBack()}>
                <IoIosArrowBack />

                <PageHeaderBackButtonLabel>
                  protocolo de treino
                </PageHeaderBackButtonLabel>
              </PageHeaderBackButton>
              {/* {allSequenciesIsFinished && (
                <FinishTrainingButton
                  disabled={isLoading}
                  onClick={handleFinishTraining}
                >
                  Finalizar Treino
                </FinishTrainingButton>
              )} */}
            </FirstScreenHeaderContainer>

            <Info>
              <InfoContent>
                <strong>Nome: </strong>
                <p>{studentData.name}</p>
              </InfoContent>

              <InfoButton type="button" onClick={() => setShowInfoModal(true)}>
                <IoMdInformationCircle color="#ffdf22" size={24} />
              </InfoButton>
            </Info>

            <Info>
              <InfoContent>
                <strong>DIVISÃO: </strong>
                <p>{division.division}</p>
              </InfoContent>

              <InfoContent>
                <strong>Segmento: </strong>
                <p>{division.muscle_group}</p>
              </InfoContent>
            </Info>
          </HeaderWrapper>

          <NewButtonContainer>
            <NewButton onClick={() => setShowSequencesModal(true)}>
              <FiPlus color="#000" size="24" />
              <NewButtonLabel>Criar Sequência</NewButtonLabel>
            </NewButton>
          </NewButtonContainer>
        </PageHeader>

        {loading ? (
          <AlertContainer>
            <MoonLoader size={30} color="#FFDF22" />
          </AlertContainer>
        ) : (
          loadSequenciesComponent
        )}
      </>
    );
  }

  const secondScreenMemo = useMemo(
    () => (
      <SecondScreen
        sequencyLabel={sequencyLabel}
        powerCreators={powerCreators}
        prescription={prescription}
        groups={groups}
        selectedSequency={selectedSequency}
        showDeleteSequencyModal={showDeleteSequencyModal}
        studentData={studentData}
        setShowDeleteSequencyModal={setShowDeleteSequencyModal}
        loadSequencies={loadSequencies}
        setScreenSwitch={setScreenSwitch}
        setShowInfoModal={setShowInfoModal}
      />
    ),
    [
      groups,
      loadSequencies,
      powerCreators,
      prescription,
      selectedSequency,
      sequencyLabel,
      showDeleteSequencyModal,
      studentData,
    ],
  );

  return (
    <>
      <InfoModal />
      <SequenceModal />
      {screenSwitch ? <FirstScreen /> : secondScreenMemo}
    </>
  );
}

function SecondScreen({
  sequencyLabel,
  powerCreators,
  prescription,
  groups,
  selectedSequency,
  showDeleteSequencyModal,
  setShowDeleteSequencyModal,
  loadSequencies,
  setScreenSwitch,
  setShowInfoModal,
  studentData,
}: {
  sequencyLabel: any;
  powerCreators: any;
  prescription: any;
  groups: any;
  selectedSequency: any;
  showDeleteSequencyModal: boolean;
  studentData: any;
  setShowDeleteSequencyModal: (v: boolean) => void;
  loadSequencies: () => void;
  setScreenSwitch: (v: boolean) => void;
  setShowInfoModal: (v: boolean) => void;
}) {
  const formRef = useRef<FormHandles>(null);
  const { goBack, location } = useHistory();
  const [defaultFormData, setDefaultFormData] = useState(
    {} as InitialFormProps,
  );

  const inputStyle = {
    input: {
      height: '3.8rem',
      padding: '0 1.7rem',
    },
    textArea: {
      height: '15rem',
      padding: '2rem',
      borderRadius: '1rem',
    },
    select: {
      placeholder: () => ({
        padding: '0 1rem',
        color: '#00000080',
      }),
      singleValue: () => ({
        padding: '0 1rem',
      }),
    },
  };

  const [showRecoveryTimeModal, setShowRecoveryTimeModal] = useState(false);
  const [activePowerCreator, setActivePowerCreator] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPowerCreator, setSelectedPowerCreator] = useState(
    {} as PowerCreator,
  );
  const [segments, setSegments] = useState<SelectProps[]>();
  const [exercises, setExercises] = useState<SelectProps[]>([
    { value: 'Aguardando segmento...', label: 'Aguardando segmento...' },
  ]);
  const [executions, setExecutions] = useState<SelectProps[]>([
    { value: '', label: 'Aguardando método...' },
  ]);
  const [selectedExecution, setSelectedExecution] = useState<SelectProps>({
    value: executions[0].value,
    label: executions[0].label,
  });
  const [equipments, setEquipments] = useState<SelectProps[]>([
    { value: 'Carregando...', label: 'Carregando...' },
  ]);
  const [accessories, setAccessories] = useState<SelectProps[]>([
    { value: 'Carregando...', label: 'Carregando...' },
  ]);
  const [catches, setCatches] = useState<SelectProps[]>([
    { value: 'Carregando...', label: 'Carregando...' },
  ]);
  const [postures, setPostures] = useState<SelectProps[]>([
    { value: 'Carregando...', label: 'Carregando...' },
  ]);
  const [axis, setAxis] = useState<SelectProps[]>([
    { value: 'Carregando...', label: 'Carregando...' },
  ]);

  const [methods, setMethods] = useState<SelectProps[]>([]);
  const [recoveryTime, setRecoveryTime] = useState('');
  const [disableTimeRecovery, setDisableTimeRecovery] = useState(true);
  const [maxChar, setMaxChar] = useState(1);

  const [exerciseId, setExerciseId] = useState<number | null>(null);

  const possibleValues = useMemo(() => ['bi-set', 'tri-set'], []);
  const isBiOrTriSet = useMemo(() => {
    const description = sequencyLabel?.split(' - ')[1]?.replaceAll(':', '');
    const result = possibleValues.includes(description);

    if (result) setShowRecoveryTimeModal(true);

    return result;
  }, [possibleValues]);

  const seriesLength = useMemo(() => {
    const value =
      selectedPowerCreator.series_number ?? defaultFormData.number_of_series;
    return value;
  }, [selectedPowerCreator, defaultFormData]);

  const handleUpdateExerciseId = (event: any) =>
    setExerciseId(event?.id || null);

  const handleCalcMaxCharLength = () => {
    const LENGTH_PER_SERIE = 3;
    const SERIES = isBiOrTriSet ? seriesLength : seriesLength - 1;

    const BARS_LENGTH = SERIES - 1;
    const total = LENGTH_PER_SERIE * SERIES + BARS_LENGTH;
    setMaxChar(total);
  };

  useEffect(() => {
    handleCalcMaxCharLength();
  }, [seriesLength]);

  const loadSelectedPowerCreatorByMethodId = useCallback(
    (methodId: string, firstRender = false) => {
      const filteredPowerCreator = powerCreators?.filter(
        (power: { id: number }) => power.id === parseInt(methodId, 10),
      )[0];

      formRef.current?.setFieldValue(
        'method_principle',
        filteredPowerCreator?.method_principle,
      );

      formRef.current?.setFieldValue(
        'technical_description',
        filteredPowerCreator?.technical_description,
      );

      formRef.current?.setFieldValue(
        'number_of_series',
        filteredPowerCreator?.series_number,
      );

      if (filteredPowerCreator.recovery) {
        setRecoveryTime(filteredPowerCreator.recovery);
      }
      if (filteredPowerCreator.execution) {
        const execution = [
          {
            value: '',
            label: 'Selecione',
          },
          {
            value: filteredPowerCreator.execution,
            label: filteredPowerCreator.execution,
          },
        ];

        setExecutions(execution);

        if (firstRender) {
          setSelectedExecution(execution[1]);
        } else {
          setSelectedExecution(execution[0]);
        }
      }

      setSelectedPowerCreator(filteredPowerCreator);
    },
    [],
  );

  useEffect(() => {
    const initialFormData = {
      ...prescription,
      segment: prescription.segment && {
        label: prescription.segment || ``,
        value: prescription.segment || ``,
      },
      exercise: prescription.exercise && {
        label: prescription.exercise || ``,
        value: prescription.exercise || ``,
      },
      acessory: prescription.acessory && {
        label: prescription.acessory || ``,
        value: prescription.acessory || ``,
      },
      equipament: prescription.equipament && {
        label: prescription.equipament || '',
        value: prescription.equipament || '',
      },
      footprint: prescription.footprint && {
        label: prescription.footprint || '',
        value: prescription.footprint || '',
      },
      posture: prescription.posture && {
        label: prescription.posture || '',
        value: prescription.posture || '',
      },
      axis: prescription.axis && {
        label: prescription.axis || '',
        value: prescription.axis || '',
      },
      method: prescription.method && {
        value: prescription.method || '',
        label: prescription.method || '',
      },
      number_of_series: prescription.number_of_series || '0',
      volume_total_number_of_series: prescription.volume_total_number_of_series
        ? prescription.volume_total_number_of_series
        : '0',
      recovery_time: prescription.recovery_time || '0',
      execution: prescription.execution,
      active_principle: prescription.method_principle,
      series_number: prescription.series_number,
      volume_total: prescription.volume_total || '0',
      total_action: prescription.total_action || '00:00:00',
      recovery_total: prescription.recovery_total || '00:00:00',
      total_time: prescription.total_time || '00:00:00',
    } as unknown as InitialFormProps;

    if (initialFormData.recovery_time) {
      formattingRecoveryGlobalText(initialFormData.recovery_time);
    }

    formRef.current?.setFieldValue(
      'number_of_series_total',
      prescription?.number_of_series,
    );

    if (initialFormData.execution) {
      const execution = [
        {
          value: '',
          label: 'Selecione',
        },
        {
          value: initialFormData.execution,
          label: initialFormData.execution,
        },
      ];

      setExecutions(execution);
      setSelectedExecution(execution[1]);
    }

    if (initialFormData.method.id) {
      loadSelectedPowerCreatorByMethodId(
        String(initialFormData.method.id),
        true,
      );
    }

    setDefaultFormData(initialFormData);
  }, [loadSelectedPowerCreatorByMethodId]);

  const loadExercises = useCallback(async (segment: string) => {
    const { data } = await api.get<ExEditorsResponse>(`ex-editors`, {
      params: {
        perpage: 9999,
        muscle_segment: segment,
      },
    });

    const mappedExercises = data[0]?.exercicies?.data?.map((exercise) => ({
      value: exercise.name,
      label: exercise.name,
      id: exercise.id,
    }));

    setExercises(mappedExercises);
  }, []);

  const loadTrainingStories = useCallback(async () => {
    const { data } = await api.get<TrainingStoresRequestData[]>(
      'training-stores',
    );

    const equipmentsData = data.filter(
      (training) => training.category === `EQUIPAMENTO`,
    );

    const mappedEquipments = equipmentsData?.map((equipment) => ({
      value: equipment.description,
      label: equipment.description,
    }));

    setEquipments(mappedEquipments);

    const acessoryData = data.filter(
      (training) => training.category === `ACESSÓRIO`,
    );

    const mappedAccessories = acessoryData.map((accessory) => ({
      value: accessory.description,
      label: accessory.description,
    }));

    setAccessories(mappedAccessories);

    const catchesData = data.filter(
      (training) => training.category === 'POSIÇÃO MÃOS / PÉS',
    );

    const mappedCatches = catchesData.map((catch_) => ({
      value: catch_.description,
      label: catch_.description,
    }));

    setCatches(mappedCatches);

    const posturesData = data.filter(
      (training) => training.category === `POSTURA`,
    );

    const mappedPostures = posturesData.map((posture) => ({
      value: posture.description,
      label: posture.description,
    }));

    setPostures(mappedPostures);

    const axisData = data.filter(
      (training) => training.category === `EIXO ARTICULAR`,
    );

    const uniqueDescriptions = new Set<string>();
    const mappedAxis = axisData
      .filter((axle) => {
        if (uniqueDescriptions.has(axle.description)) return false;
        uniqueDescriptions.add(axle.description);
        return true;
      })
      .map((axle) => ({
        value: axle.description,
        label: axle.description,
      }));

    setAxis(mappedAxis);
  }, []);

  useEffect(() => {
    const totalAction = prescription?.total_action;
    const recoveryTotal = prescription?.recovery_total;

    if (
      formRef.current?.getFieldValue('axis') === 'ALTERNADO' ||
      formRef.current?.getFieldValue('axis') === 'UNILATERAL'
    ) {
      if (totalAction) {
        formRef.current?.setFieldValue(
          'total_action',
          formatHMS(getSeconds(totalAction) * 2),
        );
      }

      if (totalAction && recoveryTotal) {
        formRef.current?.setFieldValue(
          'total_time',
          formatHMS((getSeconds(totalAction) + getSeconds(recoveryTotal)) * 2),
        );
      }
    } else if (totalAction) {
      formRef.current?.setFieldValue('total_action', totalAction);
      if (recoveryTotal) {
        formRef.current?.setFieldValue(
          'total_time',
          formatHMS(getSeconds(totalAction) + getSeconds(recoveryTotal)),
        );
      }
    }
  }, [recoveryTime, activePowerCreator]);

  useEffect(() => {
    if (
      Object.keys(selectedPowerCreator).length > 0 &&
      selectedExecution.value !== ''
    ) {
      formRef.current?.setFieldValue(
        'number_of_series',
        selectedPowerCreator?.series_number,
      );

      formRef.current?.setFieldValue(
        'number_of_series_total',
        selectedPowerCreator?.series_number,
      );

      formRef.current?.setFieldValue(
        'volume_total',
        selectedPowerCreator?.volume_total,
      );

      formRef.current?.setFieldValue(
        'recovery_total',
        selectedPowerCreator?.interval_recovery_total,
      );

      if (
        formRef.current?.getFieldValue('axis') === 'ALTERNADO' ||
        formRef.current?.getFieldValue('axis') === 'UNILATERAL'
      ) {
        console.log('total action dentro', selectedPowerCreator?.total_action);
        formRef.current?.setFieldValue(
          'total_action',
          formatHMS(getSeconds(selectedPowerCreator?.total_action ?? '0') * 2),
        );

        formRef.current?.setFieldValue(
          'total_time',
          formatHMS(
            (getSeconds(selectedPowerCreator?.total_action ?? '0') +
              getSeconds(selectedPowerCreator?.interval_recovery_total)) *
              2,
          ),
        );
      } else {
        formRef.current?.setFieldValue(
          'total_action',
          selectedPowerCreator?.total_action,
        );

        formRef.current?.setFieldValue(
          'total_time',
          formatHMS(
            getSeconds(selectedPowerCreator?.total_action ?? '0') +
              getSeconds(selectedPowerCreator?.interval_recovery_total),
          ),
        );
      }
    } else if (
      Object.keys(selectedPowerCreator).length > 0 &&
      selectedExecution.value === ''
    ) {
      formRef.current?.setFieldValue('number_of_series', '0');
      formRef.current?.setFieldValue('number_of_series_total', '0');
      formRef.current?.setFieldValue('volume_total', '0');
      formRef.current?.setFieldValue('recovery_total', '0');
      formRef.current?.setFieldValue('total_action', '0');
      formRef.current?.setFieldValue('total_time', '0');
    }
  }, [selectedPowerCreator, selectedExecution]);

  // load segments / methods
  useEffect(() => {
    const mappedGroups = groups?.map((group_: any) => ({
      value: group_,
      label: group_,
    }));

    setSegments(mappedGroups);

    const mappedPowerCreators = powerCreators?.map(
      (power: { id: any; tranning_method: any }) => ({
        value: String(power.id) || '',
        label: String(power.tranning_method) || '',
      }),
    );

    setMethods(mappedPowerCreators);
  }, []);

  useEffect(() => {
    prescription?.segment && loadExercises(prescription.segment);
  }, [loadExercises]);

  useEffect(() => {
    loadTrainingStories();
  }, [loadTrainingStories]);

  function updateTotalTimes(value: string) {
    const parsedRecoveryTotal = formRef.current
      ?.getFieldValue('recovery_time')
      ?.split('/')
      ?.map((v: string) => +v)
      ?.reduce((reducer: number, current: number) => reducer + current);

    if (value === 'ALTERNADO' || value === 'UNILATERAL') {
      formRef.current?.setFieldValue(
        'total_action',
        formatHMS(getSeconds(selectedPowerCreator?.total_action ?? '0') * 2),
      );

      formRef.current?.setFieldValue(
        'total_time',
        formatHMS(
          (getSeconds(selectedPowerCreator?.total_action ?? '0') +
            parsedRecoveryTotal ||
            getSeconds(selectedPowerCreator?.interval_recovery_total ?? '0')) *
            2,
        ),
      );
    } else {
      formRef.current?.setFieldValue(
        'total_action',
        selectedPowerCreator?.total_action,
      );

      formRef.current?.setFieldValue(
        'total_time',
        formatHMS(
          getSeconds(selectedPowerCreator?.total_action ?? '0') +
            parsedRecoveryTotal ||
            getSeconds(selectedPowerCreator?.interval_recovery_total ?? '0'),
        ),
      );
    }
  }

  const handleDeleteSequencies = async () => {
    try {
      await api.delete(
        `training-programs/1/divisions/1/sequencies/${selectedSequency.id}`,
      );

      toast.success('Sequência exluída com sucesso!');
      goBack();
    } catch (error: any) {
      console.log(error.message);
      toast.error('Erro ao exluír sequência!');
    }
  };

  function DeleteSequencyModal() {
    return (
      <Modal
        isOpen={showDeleteSequencyModal}
        setOpen={setShowDeleteSequencyModal}
        maxWidthStyled={500}
        maxHeightStyled={250}
      >
        <DeleteModalContainer>
          <p>VOCÊ DESEJA DELETAR ESTA SEQUÊNCIA?</p>

          <div>
            <button type="button" onClick={() => handleDeleteSequencies()}>
              Sim
            </button>

            <button
              type="button"
              onClick={() => setShowDeleteSequencyModal(false)}
            >
              Não
            </button>
          </div>
        </DeleteModalContainer>
      </Modal>
    );
  }

  const RecoveryTimeModal = () => {
    return (
      <Modal
        isOpen={showRecoveryTimeModal}
        setOpen={setShowRecoveryTimeModal}
        maxHeightStyled="350px"
        maxWidthStyled="400px"
      >
        <RecoveryTimeModalContainer>
          <p>
            <strong>
              Deseja alterar o tempo de recuperação a&nbsp;
              {sequencyLabel.split(' - ')[1]}?
            </strong>
          </p>

          <div>
            <button type="button" onClick={inputFocus}>
              Sim
            </button>
            <button
              type="button"
              onClick={() => {
                setDisableTimeRecovery(true);
                setShowRecoveryTimeModal(false);
              }}
            >
              Não
            </button>
          </div>
        </RecoveryTimeModalContainer>
      </Modal>
    );
  };

  function inputFocus() {
    const input = formRef.current?.getFieldRef('recovery_time');

    input.focus();

    setDisableTimeRecovery(false);
    setShowRecoveryTimeModal(false);
    // setShowRecoveryTimeModal(true);
  }

  const formattingRecoveryGlobalText = (value: string) => {
    const txtFaseOne = value.replace(/([0-9])([0-9])([0-9])/g, `$1$2$3/`);
    const txtFaseTwo = txtFaseOne.split('/');
    const txtFaseThree = txtFaseTwo.filter((item) => item !== '');
    const txtFaseFour = txtFaseThree.join('/');
    setRecoveryTime(txtFaseFour);
  };

  const handleSubmit = useCallback(
    async (data: DataFormProps) => {
      setIsLoading(true);

      // eslint-disable-next-line @typescript-eslint/no-unused-vars

      const formData = {
        number_of_series: data.number_of_series,
        charge: data.charge,
        technical_description: data.technical_description,
        segment: data.segment,
        exercise: data.exercise,
        equipament: data.equipament,
        acessory: data.acessory,
        footprint: data.footprint,
        posture: data.posture,
        axis: data.axis,
        method: data.method,
        execution: selectedExecution.value,
        recovery_time: data.recovery_time
          .replace(/_/g, '')
          .split('/')
          .filter(Boolean)
          .join('/'),
        ex_editor_id: exerciseId,
        total_time: data.total_time,
        total_action: data.total_action,
        method_principle: data.method_principle,
        volume_total: data.volume_total,
        recovery_total: data.recovery_total,
      };

      const schema = Yup.object().shape({
        acessory: Yup.string().required('O campo é obrigatório'),
        axis: Yup.string().required('O campo é obrigatório'),
        charge: Yup.string().required('O campo é obrigatório'),
        equipament: Yup.string().required('O campo é obrigatório'),
        execution: Yup.string().required('O campo é obrigatório'),
        exercise: Yup.string().required('O campo é obrigatório'),
        footprint: Yup.string().required('O campo é obrigatório'),
        method: Yup.string().required('O campo é obrigatório'),
        number_of_series: Yup.number().required('O campo é obrigatório'),
        posture: Yup.string().required('O campo é obrigatório'),
        recovery_time: Yup.string().required('O campo é obrigatório'),
        segment: Yup.string().required('O campo é obrigatório'),
        technical_description: Yup.string().required('O campo é obrigatório'),
        total_time: Yup.string().required('O campo é obrigatório'),
        total_action: Yup.string().required('O campo é obrigatório'),
        method_principle: Yup.string().required('O campo é obrigatório'),
        volume_total: Yup.string().required('O campo é obrigatório'),
        recovery_total: Yup.string().required('O campo é obrigatório'),
        // volume_total_number_of_series: Yup.string().required(
        //   'O campo é obrigatório',
        // ),
      });

      try {
        await schema.validate(formData, { abortEarly: false });

        await api.put(`/prescriptions/${prescription.id}`, formData);

        toast.success('Prescrição alterada com sucesso!');

        loadSequencies();

        setScreenSwitch(true);
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          console.log(errors);

          formRef.current?.setErrors(errors);

          toast.error('Preencha os campos obrigatórios e tente novamente!');
        }
      }
      setIsLoading(false);
    },
    [selectedPowerCreator, selectedExecution, exerciseId],
  );

  useEffect(() => {
    isBiOrTriSet && setShowRecoveryTimeModal(true);
  }, [isBiOrTriSet]);

  return (
    <>
      <RecoveryTimeModal />
      <DeleteSequencyModal />

      <SecondPageHeader>
        <SecondaryHeaderLine>
          <SecondaryHeaderBackButton onClick={() => setScreenSwitch(true)}>
            <IoIosArrowBack />

            <p>{sequencyLabel}</p>
          </SecondaryHeaderBackButton>

          <SecondaryHeaderContinueButton
            disabled={isLoading}
            onClick={() => {
              formRef.current?.submitForm();
            }}
          >
            Salvar
          </SecondaryHeaderContinueButton>
        </SecondaryHeaderLine>

        <SecondaryHeaderLine>
          <SecondaryHeaderInfo>
            <div>
              <strong>Nome: </strong>
              <p>{studentData.name}</p>
            </div>

            <InfoButton type="button" onClick={() => setShowInfoModal(true)}>
              <IoMdInformationCircle color="#ffdf22" size={24} />
            </InfoButton>

            <InfoButton
              type="button"
              onClick={() => setShowDeleteSequencyModal(true)}
            >
              <IoIosTrash color="red" size={24} />
            </InfoButton>
          </SecondaryHeaderInfo>
        </SecondaryHeaderLine>
      </SecondPageHeader>

      <Section>
        <Form
          ref={formRef}
          onSubmit={handleSubmit}
          initialData={defaultFormData}
        >
          <RowContainer>
            <InputSet>
              <InputLabel>Segmento:</InputLabel>
              <Select
                key={`rerender_key_${defaultFormData.segment}`}
                name="segment"
                options={segments}
                onChange={(e) => {
                  e && loadExercises(e.label);
                  formRef.current?.clearField('exercise');
                }}
                isSearchable={false}
                placeholder="Selecione"
                styles={inputStyle.select}
              />
            </InputSet>

            <InputSet>
              <InputLabel>Exercício:</InputLabel>
              <Select
                key={`rerender_key_${defaultFormData.exercise}`}
                name="exercise"
                options={exercises}
                onChange={handleUpdateExerciseId}
                isSearchable={false}
                placeholder="Selecione"
                styles={inputStyle.select}
              />
            </InputSet>
          </RowContainer>

          <RowContainer>
            <InputSet>
              <InputLabel>Equipamento:</InputLabel>
              <Select
                key={`rerender_key_${defaultFormData.equipament}`}
                name="equipament"
                isSearchable={false}
                options={equipments}
                placeholder="Selecione"
                styles={inputStyle.select}
              />
            </InputSet>

            <InputSet>
              <InputLabel>Acessório:</InputLabel>
              <Select
                key={`rerender_key_${defaultFormData.acessory}`}
                name="acessory"
                isSearchable={false}
                options={accessories}
                placeholder="Selecione"
                styles={inputStyle.select}
              />
            </InputSet>
          </RowContainer>

          <RowContainer>
            <InputSet>
              <InputLabel>Posição mãos/pés:</InputLabel>
              <Select
                key={`rerender_key_${defaultFormData.footprint}`}
                name="footprint"
                isSearchable={false}
                options={catches}
                placeholder="Selecione"
                styles={inputStyle.select}
              />
            </InputSet>

            <InputSet>
              <InputLabel>Postura:</InputLabel>
              <Select
                key={`rerender_key_${defaultFormData.posture}`}
                name="posture"
                isSearchable={false}
                options={postures}
                placeholder="Selecione"
                styles={inputStyle.select}
              />
            </InputSet>
          </RowContainer>

          <RowContainer>
            <InputSet>
              <InputLabel>Eixo:</InputLabel>
              <Select
                key={`rerender_key_${defaultFormData.axis}`}
                name="axis"
                onChange={(e) => updateTotalTimes(e?.value)}
                isSearchable={false}
                options={axis}
                placeholder="Selecione"
                styles={inputStyle.select}
              />
            </InputSet>

            <InputSet>
              <InputLabel>Método:</InputLabel>
              <Select
                key={`rerender_key_${defaultFormData.method}`}
                onChange={(e) =>
                  // loadSelectedPowerCreatorByMethodName(e?.value)
                  loadSelectedPowerCreatorByMethodId(e?.value)
                }
                options={methods}
                name="method"
                isSearchable={false}
                placeholder="Selecione"
                styles={inputStyle.select}
              />
            </InputSet>
          </RowContainer>

          <RowContainer>
            <InputSet>
              <InputLabel>Execução:</InputLabel>
              <Select
                key={`rerender_key_${defaultFormData.execution}`}
                onChange={(e) => setSelectedExecution(e as SelectProps)}
                options={executions}
                name="execution"
                isSearchable={false}
                placeholder="Selecione"
                styles={inputStyle.select}
                value={selectedExecution}
              />
            </InputSet>

            <InputSet title={defaultFormData.method_principle}>
              <InputLabel>Nº de séries:</InputLabel>
              <Input
                disabled
                name="number_of_series"
                fontSize="1.5rem"
                containerStyle={inputStyle.input}
              />
            </InputSet>
          </RowContainer>

          <RowContainer>
            <InputSet>
              <InputLabel>Carga (Kg):</InputLabel>
              <Input
                type="text"
                name="charge"
                fontSize="1.5rem"
                placeholder="0 kg"
                containerStyle={inputStyle.input}
              />
            </InputSet>

            <InputSet>
              <InputLabel>Tempo de recuperação:</InputLabel>
              <Input
                name="recovery_time"
                disabled={disableTimeRecovery}
                placeholder="060/030/090"
                maxLength={maxChar}
                value={recoveryTime}
                onChange={(e) => {
                  const { value } = e.target;
                  formattingRecoveryGlobalText(value);
                }}
                containerStyle={inputStyle.input}
                fontSize="1.5rem"
              />
            </InputSet>
          </RowContainer>

          <RowContainer completeRow>
            <InputSet title={defaultFormData.method_principle}>
              <InputLabel>Princípio ativo:</InputLabel>
              <Input
                name="method_principle"
                disabled
                placeholder="Princípio ativo"
                fontSize="1.5rem"
                containerStyle={inputStyle.input}
              />
            </InputSet>
          </RowContainer>

          <RowContainer completeRow>
            <InputSet>
              <InputLabel>Descrição técnica:</InputLabel>
              <Textarea
                name="technical_description"
                disabled
                containerStyle={inputStyle.textArea}
              />
            </InputSet>

            <Divider />
          </RowContainer>

          <PowerCreatorButton
            active={activePowerCreator}
            onClick={() => setActivePowerCreator((prev) => !prev)}
          >
            <div style={{ backgroundColor: 'red' }}>
              <ImageLogoAtomBlack />
            </div>
          </PowerCreatorButton>

          <PowerCreatorView active={activePowerCreator}>
            <fieldset>
              <label>Volume total Nº de séries:</label>

              <Input name="number_of_series_total" disabled />
            </fieldset>

            <fieldset>
              <label>Volume total Nº de repetições:</label>

              <Input name="volume_total" disabled />
            </fieldset>

            <fieldset>
              <label>Tempo de ação total:</label>

              <Input name="total_action" disabled />
            </fieldset>

            <fieldset>
              <label>Intervalo de recuperação total:</label>

              <Input name="recovery_total" disabled />
            </fieldset>

            <fieldset>
              <label>Tempo total:</label>

              <Input name="total_time" disabled />
            </fieldset>
          </PowerCreatorView>
        </Form>
      </Section>
    </>
  );
}
